<template>
  <money
    v-model="internalValue"
    v-bind="moneyConfig"
    :disabled="disabled"
    class="form-control"
    @input="onInput"
    @keydown.native="onKeydown"
    @paste.native="onPaste"
  />
</template>

<script>
import { Money } from "v-money";

export default {
  name: "QuantidadeDecimalInput",
  components: {
    Money,
  },
  props: {
    value: {
      type: [Number, String, null],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    precision: {
      type: Number,
      default: 3,
    },
    max: {
      type: Number,
      default: 999.999,
    },
  },
  data() {
    return {
      internalValue: null,
    };
  },
  computed: {
    moneyConfig() {
      return {
        decimal: ",",
        masked: false,
        prefix: "",
        precision: this.precision,
        thousands: ".",
      };
    },

    maxDigits() {
      const safePrecision = Math.max(0, Number(this.precision) || 0);
      const safeMax = Number.isFinite(Number(this.max)) ? Number(this.max) : 0;
      const maxAsString = Math.abs(safeMax).toFixed(safePrecision);
      const [integerPart] = maxAsString.split(".");
      const integerDigits = integerPart.replace(/\D/g, "").length;

      return integerDigits + safePrecision;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.internalValue = this.normalizeAndLimit(newValue);
      },
    },
  },
  methods: {
    parseValue(value) {
      if (value === null || value === undefined || value === "") {
        return null;
      }

      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }

      if (typeof value === "string") {
        let str = value.trim();
        if (!str) return null;

        if (str.includes(".") && str.includes(",")) {
          if (str.lastIndexOf(",") > str.lastIndexOf(".")) {
            str = str.replace(/\./g, "").replace(",", ".");
          } else {
            str = str.replace(/,/g, "");
          }
        } else if (str.includes(",")) {
          str = str.replace(",", ".");
        }

        const parsed = parseFloat(str);
        return Number.isFinite(parsed) ? parsed : null;
      }

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },

    normalizeAndLimit(value) {
      const parsed = this.parseValue(value);
      if (parsed === null) return null;

      const fixedPrecision = Math.max(0, Number(this.precision) || 0);
      const capped = Math.min(parsed, this.max);

      return Number(capped.toFixed(fixedPrecision));
    },

    onInput(value) {
      const normalized = this.normalizeAndLimit(value);
      this.internalValue = normalized;
      this.$emit("input", normalized);
    },

    getOnlyDigits(value) {
      return String(value || "").replace(/\D/g, "");
    },

    onKeydown(event) {
      if (this.disabled) {
        return;
      }

      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];

      if (allowedKeys.includes(event.key)) {
        return;
      }

      const isShortcut =
        (event.ctrlKey || event.metaKey) &&
        ["a", "c", "v", "x"].includes(String(event.key || "").toLowerCase());

      if (isShortcut) {
        return;
      }

      const isDigit = /^\d$/.test(event.key);
      if (!isDigit) {
        event.preventDefault();
        return;
      }

      const target = event.target;
      const selectionStart = Number(target.selectionStart || 0);
      const selectionEnd = Number(target.selectionEnd || 0);
      const currentValue = String(target.value || "");
      const selectedText = currentValue.slice(selectionStart, selectionEnd);

      const currentDigits = this.getOnlyDigits(currentValue).length;
      const selectedDigits = this.getOnlyDigits(selectedText).length;
      const nextDigitsLength = currentDigits - selectedDigits + 1;

      if (nextDigitsLength > this.maxDigits) {
        event.preventDefault();
      }
    },

    onPaste(event) {
      if (this.disabled) {
        return;
      }

      event.preventDefault();

      const pastedText =
        (event.clipboardData && event.clipboardData.getData("text")) || "";

      const digits = this.getOnlyDigits(pastedText).slice(0, this.maxDigits);
      if (!digits) {
        this.internalValue = null;
        this.$emit("input", null);
        return;
      }

      const safePrecision = Math.max(0, Number(this.precision) || 0);
      const scale = Math.pow(10, safePrecision);
      const numericValue = Number(digits) / scale;
      const normalized = this.normalizeAndLimit(numericValue);

      this.internalValue = normalized;
      this.$emit("input", normalized);
    },
  },
};
</script>
